import React from 'react';
import { Nullable, Therapist, TherapySession, TimelineDocument } from '../interfaces/Frameworks';
import { ClientRecordView, ClientRecordViewProps } from './ClientRecordView';

type TimelineCallbacks = Pick<ClientRecordViewProps,
    'onSaveSessionTitle' | 'onSaveSessionDate' | 'onSaveSessionClinicalNotes' |
    'onSaveSessionTranscriptUtterances' | 'onSaveSessionPlacedTags' | 'onSaveTherapistSessionTags' |
    'onEnsureSessionWaveform' | 'onRetrySessionTranscription' | 'onDeleteSession' |
    'onCreateTimelineClinicalNote' | 'onSaveTimelineClinicalNote' | 'onSaveTimelineDocumentTitle' |
    'onSaveTimelineDocumentDate' | 'onUploadTimelineFile' | 'onGetTimelineFileDownloadUrl' |
    'onDownloadTimelineFile' | 'onDeleteTimelineDocument'>;

// Organisation supplies the same timeline operations used by Client records.
export interface TeamMemberTimelineData extends TimelineCallbacks {
    currentTherapist: Nullable<Therapist>;
    therapists: Array<Therapist>;
    sessions: Array<TherapySession>;
    documents: Array<TimelineDocument>;
    canUseAudioScribeAndReports: boolean;
    canUseTimelineDocuments: boolean;
}

type TeamMemberTimelineViewProps = {
    member: Therapist;
    onBack: () => void;
    teamTimeline: TeamMemberTimelineData;
};

// Hidden Client-only controls are inert; Team writes use the explicit selectedTeamMember identity.
const unusedAction = () => {};
const unusedAsyncAction = async () => {};

// Reuse the full timeline renderer while exposing only Team-relevant data and actions.
export function TeamMemberTimelineView({ member, onBack, teamTimeline }: TeamMemberTimelineViewProps) {
    const { sessions, documents, ...timelineProps } = teamTimeline;
    return (
        <ClientRecordView
            {...timelineProps}
            selectedTeamMember={member}
            authenticatedUserId={teamTimeline.currentTherapist?.id || ''}
            authenticatedUserType='therapist'
            allClients={[]}
            activityTimelineByClientId={{}}
            sessionMinutesTimelineByClientId={{}}
            signUpTimeByClientId={{}}
            stageByClientId={{}}
            assessmentDefinitions={[]}
            selectedClientAssessmentResponses={[]}
            isLoadingSelectedClientAssessments={false}
            therapySessions={sessions}
            therapySessionReports={[]}
            timelineDocuments={documents}
            reportTemplates={[]}
            speechNeeds={[]}
            isEditing={false}
            editingClientData={null}
            showFamilySponsorship={false}
            familySponsorshipStatus={null}
            isLoadingFamilySponsorship={false}
            familySponsorshipError=''
            canManageFamilySponsorship={false}
            onBack={onBack}
            onImportSessionFile={async () => ''}
            onGenerateSessionReport={async () => ''}
            onSaveSessionReportText={unusedAsyncAction}
            onDeleteSessionReport={unusedAsyncAction}
            onStartEditing={unusedAction}
            onDiscardEditing={unusedAction}
            onSaveClient={unusedAction}
            onAssignMyClient={unusedAction}
            onEditClientChange={unusedAction}
            onEditClientCustomPropertiesChange={unusedAction}
            onEditClientSpeechNeedsChange={unusedAction}
            onCommsCampaignEnrollmentChange={unusedAction}
            onSelectClient={unusedAction}
            onSetFamilySponsorship={unusedAsyncAction}
        />
    );
}
